import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { Play, X } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const AuditsDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [stats, setStats] = useState({
    upcoming: 0,
    overdue: 0,
    completed: 0,
    capaPending: 0
  });
  const [upcomingAudits, setUpcomingAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickStartModal, setShowQuickStartModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [schedulesRes, recordsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/audit-schedules`, { headers }),
        axios.get(`${API}/audit-records`, { headers }),
        axios.get(`${API}/audit-templates`, { headers })
      ]);

      const schedules = schedulesRes.data;
      const records = recordsRes.data;

      // Calculate stats
      const now = new Date();
      const upcoming = schedules.filter(s => {
        const schedDate = new Date(s.scheduled_date);
        return schedDate > now && s.status === 'Scheduled';
      }).length;

      const overdue = schedules.filter(s => {
        const schedDate = new Date(s.scheduled_date);
        return schedDate < now && s.status === 'Scheduled';
      }).length;

      const completed = records.filter(r => r.workflow_status === 'Closed').length;
      const capaPending = records.filter(r => r.workflow_status === 'CAPA Outstanding').length;

      setStats({ upcoming, overdue, completed, capaPending });
      setUpcomingAudits(schedules.filter(s => s.status === 'Scheduled').slice(0, 5));
      setTemplates(templatesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch audit data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartAuditFromSchedule = async (schedule) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Create audit record from schedule
      const auditData = {
        audit_title: schedule.audit_title,
        audit_type: schedule.audit_type,
        template_id: schedule.template_id,
        area: schedule.area || '',
        location: schedule.location || '',
        auditor_id: user.id,
        auditor_name: user.name,
        lead_auditor_id: schedule.lead_auditor_id,
        lead_auditor_name: schedule.auditor_name,
        scheduled_date: schedule.scheduled_date,
        checklist_items: [],
        findings: []
      };

      const response = await axios.post(`${API}/audit-records`, auditData, { headers });
      
      // Update schedule status
      await axios.put(
        `${API}/audit-schedules/${schedule.id}`,
        { status: 'In Progress' },
        { headers }
      );

      // Navigate to conduct audit
      navigate(`/audits/conduct/${response.data.id}`);
    } catch (err) {
      showError('Failed to start audit: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleQuickStartAudit = () => {
    if (!selectedTemplate) {
      showWarning('Please select a template');
      return;
    }

    // Navigate to conduct audit with template ID as query param
    navigate(`/audits/conduct?template_id=${selectedTemplate.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800">🔍 Audits & Inspections</h1>
          <p className="text-gray-600 mt-1">Manage compliance audits and safety inspections</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-semibold">Upcoming Audits</p>
                <p className="text-3xl font-bold text-blue-700 mt-2">{stats.upcoming}</p>
              </div>
              <div className="text-blue-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-red-50 rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-semibold">Overdue</p>
                <p className="text-3xl font-bold text-red-700 mt-2">{stats.overdue}</p>
              </div>
              <div className="text-red-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-semibold">Completed</p>
                <p className="text-3xl font-bold text-green-700 mt-2">{stats.completed}</p>
              </div>
              <div className="text-green-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-semibold">CAPA Pending</p>
                <p className="text-3xl font-bold text-yellow-700 mt-2">{stats.capaPending}</p>
              </div>
              <div className="text-yellow-500">
                <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <button
            onClick={() => setShowQuickStartModal(true)}
            className="bg-green-600 text-white p-6 rounded-xl shadow-md hover:bg-green-700 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Play className="w-10 h-10" />
              <div className="text-left">
                <h3 className="text-lg font-bold">Start Audit</h3>
                <p className="text-sm text-green-100">Begin immediately</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/audits/schedule')}
            className="bg-purple-600 text-white p-6 rounded-xl shadow-md hover:bg-purple-700 transition-colors"
          >
            <div className="flex items-center gap-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <div className="text-left">
                <h3 className="text-lg font-bold">Schedule Audit</h3>
                <p className="text-sm text-purple-100">Plan new inspection</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/audits/templates')}
            className="bg-indigo-600 text-white p-6 rounded-xl shadow-md hover:bg-indigo-700 transition-colors"
          >
            <div className="flex items-center gap-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="text-left">
                <h3 className="text-lg font-bold">Audit Templates</h3>
                <p className="text-sm text-indigo-100">Manage checklists</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/audits/records')}
            className="bg-teal-600 text-white p-6 rounded-xl shadow-md hover:bg-teal-700 transition-colors"
          >
            <div className="flex items-center gap-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <div className="text-left">
                <h3 className="text-lg font-bold">Audit Records</h3>
                <p className="text-sm text-teal-100">View all audits</p>
              </div>
            </div>
          </button>
        </div>

        {/* Upcoming Audits List */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Scheduled Audits</h2>
          {upcomingAudits.length > 0 ? (
            <div className="space-y-3">
              {upcomingAudits.map(audit => (
                <div key={audit.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{audit.audit_title}</h3>
                    <p className="text-sm text-gray-600">Auditor: {audit.auditor_name}</p>
                    {audit.location && <p className="text-xs text-gray-500">Location: {audit.location}</p>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-700">
                        {new Date(audit.scheduled_date).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(audit.scheduled_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleStartAuditFromSchedule(audit)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Play className="w-4 h-4" />
                      <span className="font-semibold">Start Audit</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-600 text-center py-8">No scheduled audits</p>
          )}
        </div>

        {/* Quick Start Modal */}
        {showQuickStartModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Start Audit</h2>
                  <p className="text-sm text-gray-600 mt-1">Select a template to begin</p>
                </div>
                <button
                  onClick={() => setShowQuickStartModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {templates.length > 0 ? (
                  <div className="space-y-3">
                    {templates.map(template => (
                      <label
                        key={template.id}
                        className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedTemplate?.id === template.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="template"
                          checked={selectedTemplate?.id === template.id}
                          onChange={() => setSelectedTemplate(template)}
                          className="mt-1 w-5 h-5 text-green-600 border-gray-300 focus:ring-2 focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{template.template_name}</h3>
                          <p className="text-sm text-gray-600 mt-1">{template.audit_type}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {template.checklist_items?.length || 0} checklist items
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">No templates available</p>
                    <button
                      onClick={() => navigate('/audits/templates')}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Create a template first
                    </button>
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                <p className="text-sm text-gray-600">
                  {selectedTemplate ? `Selected: ${selectedTemplate.template_name}` : 'Please select a template'}
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowQuickStartModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleQuickStartAudit}
                    disabled={!selectedTemplate}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Play className="w-4 h-4" />
                    <span>Start Audit</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default AuditsDashboard;
